import AppES from './AppES.jsx';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import Practice from './Practice';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import emailjs from '@emailjs/browser';

/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);
const WA = 'https://api.whatsapp.com/send?phone=553198011835&text=';

// speaking_club and individual_vip are legacy plans — no longer offered to new
// students, but kept here so existing students on those plans keep their
// current access unchanged. New students get kickstart or elite_squad only.
const PLAN_ACCESS = {
  speaking_club:  ['Speaking Club', 'Plantão de dúvidas'],
  individual_vip: ['Individual lesson', 'Plantão de dúvidas'],
  elite_squad:    ['Individual lesson', 'Elite Squad Individual', 'Elite Squad', 'Speaking Club', 'Plantão de dúvidas'],
  kickstart:      ['Elite Squad', 'Speaking Club'],
};
const PLAN_LABELS = { speaking_club: 'Speaking Club', individual_vip: 'Individual VIP', elite_squad: 'Elite Squad', kickstart: 'Kickstart' };
const PLAN_BADGE  = {
  speaking_club:  { bg: '#dcfce7', color: '#166534' },
  individual_vip: { bg: '#dbeafe', color: '#1d4ed8' },
  kickstart:      { bg: '#dcfce7', color: '#16a349' },
  elite_squad:    { bg: '#fef3c7', color: '#d97706' },
};

// ── hint field added: shown on locked badges so students know how to unlock ──
const BADGES = [
  { id: 'first',     icon: '🎯', label: 'Primeira Aula',  desc: 'Bem-vindo à Student Portal!',                             hint: 'Assista sua 1ª aula',                        color: '#ff6a00', check: (d) => d.totalAtt >= 1 },
  { id: 'five',      icon: '⭐', label: '5 Aulas',        desc: 'Consistência é a chave!',                    hint: 'Assista 5 aulas no total',                   color: '#f59e0b', check: (d) => d.totalAtt >= 5 },
  { id: 'ten',       icon: '🔥', label: '10 Aulas',       desc: 'Você está pegando o ritmo!',                 hint: 'Assista 10 aulas no total',                  color: '#ef4444', check: (d) => d.totalAtt >= 10 },
  { id: 'tf',        icon: '💪', label: '25 Aulas',       desc: 'Dedicação impressionante!',                  hint: 'Assista 25 aulas no total',                  color: '#8b5cf6', check: (d) => d.totalAtt >= 25 },
  { id: 'fifty',     icon: '🏆', label: '50 Aulas',       desc: 'Mestre em formação!',                        hint: 'Assista 50 aulas no total',                  color: '#1a2a6c', check: (d) => d.totalAtt >= 50 },
  { id: 'hundred',   icon: '💎', label: '100 Aulas',      desc: 'Lenda da Student Portal!',                                    hint: 'Assista 100 aulas no total',                 color: '#0d9488', check: (d) => d.totalAtt >= 100 },
  { id: 'launch',    icon: '🚀', label: 'Decolagem',      desc: 'Primeira aula marcada em menos de 24h!',     hint: 'Agende a 1ª aula em até 24h do cadastro',    color: '#2563eb', check: (d) => d.decolagem },
  { id: 'loyal',     icon: '🤝', label: 'Fiel',           desc: '8 semanas consecutivas com aula!',           hint: 'Tenha presença por 8 semanas seguidas',      color: '#16a34a', check: (d) => d.maxStreak >= 8 },
  { id: 'unstop',    icon: '⚡', label: 'Imparável',      desc: '12 semanas seguidas — extraordinário!',      hint: 'Mantenha presença por 12 semanas seguidas',  color: '#dc2626', check: (d) => d.maxStreak >= 12 },
  { id: 'punct',     icon: '🗓️', label: 'Pontualidade',   desc: '5+ aulas sem nenhum cancelamento!',          hint: 'Assista 5 aulas sem cancelar nenhuma',       color: '#0284c7', check: (d) => d.totalAtt >= 5 && d.totalCancellations === 0 },
  { id: 'scstar',    icon: '🌟', label: 'Speaking Star',  desc: '5 aulas de Speaking Club!',                  hint: 'Participe do Speaking Club 5 vezes',         color: '#7c3aed', check: (d) => d.scAtt >= 5 },
  { id: 'confident', icon: '🎤', label: 'Confiante',      desc: '10 aulas de Speaking Club!',                 hint: 'Participe do Speaking Club 10 vezes',        color: '#be185d', check: (d) => d.scAtt >= 10 },
  { id: 'levelup',   icon: '📈', label: 'Evoluindo',      desc: 'Subiu de nível — crescimento real!',         hint: 'Avance de nível com seu professor',           color: '#d97706', check: (d) => d.hasLevelUp },
  { id: 'elite',     icon: '👑', label: 'Elite',          desc: '12 meses de Elite Squad completados!',       hint: 'Complete os 12 meses do Elite Squad',        color: '#1a2a6c', check: (d) => d.eliteCompleted },
  { id: 'game_first', icon: '🎮', label: 'Primeira Jogada',  desc: 'Bem-vindo aos mini games!',                  hint: 'Complete seu primeiro mini game',              color: '#7c3aed', check: (d) => d.gamesTotal >= 1 },
  { id: 'game_7',     icon: '🔥', label: '7 Dias de Jogos',  desc: 'Uma semana de prática diária!',              hint: 'Complete jogos por 7 dias seguidos',           color: '#ef4444', check: (d) => d.gameStreak >= 7 },
  { id: 'game_30d',   icon: '💪', label: '30 Dias de Jogos', desc: 'Um mês de dedicação!',                       hint: 'Complete jogos por 30 dias seguidos',          color: '#8b5cf6', check: (d) => d.gameStreak >= 30 },
  { id: 'game_60d',   icon: '⚡', label: '60 Dias de Jogos', desc: 'Dois meses imparável!',                      hint: 'Complete jogos por 60 dias seguidos',          color: '#f59e0b', check: (d) => d.gameStreak >= 60 },
  { id: 'game_90d',   icon: '🏆', label: '90 Dias de Jogos', desc: '3 meses — lenda dos jogos!',                 hint: 'Complete jogos por 90 dias seguidos',          color: '#0d9488', check: (d) => d.gameStreak >= 90 },
  { id: 'game_30t',   icon: '🧠', label: 'Dedicado',         desc: '30 jogos concluídos no total!',              hint: 'Complete 30 mini games no total',              color: '#1a2a6c', check: (d) => d.gamesTotal >= 30 },
  { id: 'game_all',   icon: '🎯', label: 'Completo',         desc: 'Jogou todos os 5 tipos de jogo!',            hint: 'Jogue todos os 5 tipos de mini game',          color: '#16a34a', check: (d) => d.gameTypesPlayed >= 5 },
  { id: 'mission_1',  icon: '🌍', label: 'Missão Cumprida',  desc: 'Primeira missão real concluída!',            hint: 'Complete sua primeira missão real',            color: '#2563eb', check: (d) => d.missionsTotal >= 1 },
  { id: 'mission_5',  icon: '🏅', label: 'Agente de Campo',  desc: '5 missões reais completadas!',               hint: 'Complete 5 missões no mundo real',             color: '#d97706', check: (d) => d.missionsTotal >= 5 },
];

const CHECKIN_QUESTIONS = [
  { q: 'Como você está se sentindo com seu inglês esta semana?', opts: ['🚀 Evoluindo muito!', '😊 Indo bem', '😐 Estável', '💪 Preciso praticar mais'] },
  { q: 'Você conseguiu praticar inglês fora das aulas?', opts: ['✅ Todo dia', '🔄 Algumas vezes', '❌ Não consegui', '📱 Só assisti séries'] },
  { q: 'Qual foi o maior desafio desta semana no inglês?', opts: ['🗣️ Falar com fluidez', '👂 Entender nativos', '📝 Gramática', '💭 Pensar em inglês'] },
  { q: 'Como você se preparou para suas aulas?', opts: ['📚 Estudei bastante', '🎯 Revisei o material', '⚡ Fui no improviso', '😴 Não consegui preparar'] },
  { q: 'Você está mais confiante para falar inglês do que há um mês?', opts: ['🙌 Muito mais!', '📈 Um pouco mais', '➡️ Igual', '🤔 Não tenho certeza'] },
  { q: 'O que mais te motiva a aprender inglês?', opts: ['💼 Carreira', '✈️ Viagens', '🌍 Cultura global', '👥 Networking internacional'] },
  { q: 'Como foi sua participação nas aulas esta semana?', opts: ['🎤 Falei muito!', '😊 Participei bem', '😐 Poderia ter feito mais', '😔 Fui mais tímido(a)'] },
  { q: 'Você usou inglês em alguma situação real esta semana?', opts: ['✅ Várias vezes', '🔄 Uma ou duas vezes', '📺 Só consumindo conteúdo', '❌ Sem oportunidade'] },
  { q: 'Como você avalia seu ritmo de evolução?', opts: ['🚀 Rápido!', '📈 Constante', '🐢 Lento mas firme', '⚡ Precisa acelerar'] },
  { q: 'O que você quer focar nas próximas aulas?', opts: ['🗣️ Speaking', '👂 Listening', '📝 Vocabulário', '🎯 Situações reais'] },
  { q: 'Você assistiu algum conteúdo em inglês esta semana?', opts: ['📺 Séries/filmes', '🎵 Músicas', '📰 Notícias em inglês', '❌ Não consegui'] },
  { q: 'Como está seu vocabulário em inglês?', opts: ['📚 Crescendo muito', '🔄 Usando palavras novas', '😐 Estável', '💪 Preciso de mais palavras'] },
  { q: 'Você tem conseguido pensar em inglês naturalmente?', opts: ['✅ Cada vez mais!', '🔄 Às vezes', '🤔 Ainda traduzo mentalmente', '❌ Ainda é difícil'] },
  { q: 'Qual é sua meta para a próxima semana?', opts: ['📅 Assistir todas as aulas', '🗣️ Falar mais nas aulas', '📚 Estudar mais fora', '🎯 Usar inglês no trabalho'] },
  { q: 'Como você está se sentindo sobre sua evolução geral?', opts: ['🙌 Valendo muito!', '📈 Vendo progresso', '🤔 Preciso de mais tempo', '💪 Determinado(a) a evoluir'] },
];

const REFLECTION_PROMPTS = {
  Beginner: [
    'Describe your perfect weekend in English. Use simple sentences — 3 to 5 is great!',
    'What is your favourite food? Write a few sentences about why you love it.',
    'Describe someone important in your life. Who are they and why do they matter to you?',
    'What do you do to relax after a busy day? Describe it in English.',
    'If you could visit one country, where would it be and why?',
    'What is your daily morning routine? Describe it step by step.',
    'Talk about a hobby you enjoy. Why do you like it and when do you do it?',
    'What is your favourite movie or TV show? Try to explain what it is about.',
    'Describe the city or town you live in. What do you like and dislike about it?',
    'What are three things you are grateful for this week? Write in English.',
  ],
  Intermediate: [
    'Describe a challenge you faced recently and how you overcame it. What did you learn?',
    'If you could have dinner with any person in history, who would it be and what would you ask them?',
    'What is the most important skill for success in your career and why?',
    'Talk about a time when you had to make a difficult decision. What happened and what did you choose?',
    'How has learning English changed — or will change — your life?',
    'Describe your ideal work environment. What makes a truly great workplace?',
    'What global issue concerns you the most right now and why?',
    'Talk about a book, podcast or documentary that impacted you. What did you take from it?',
    'If you could change one thing about your city, what would it be and why?',
    'What does success mean to you personally — beyond money or titles?',
  ],
};

const CANCEL_REASONS = [
  '😔 Compromisso de trabalho',
  '🤒 Não estou me sentindo bem',
  '🚗 Imprevisto pessoal',
  '📅 Esqueci de cancelar antes',
  '⚡ Problema técnico',
  '🔄 Vou agendar outra aula',
];

const TIPS = [
  { icon: '🎙️', title: 'SotaQ: Seu Lab de Pronúncia', text: 'Treine seu sotaque 24h/dia com IA exclusiva. Ferramenta gratuita criada especialmente para alunos Student Portal.', cta: 'Testar Grátis →', link: 'https://sotaq.netlify.app/' },
  { icon: '🔥', title: 'Protocolo Active Output™', text: '80% do tempo de cada aula é você falando. Sem passividade — o Student Portal Framework foi desenhado para colocá-lo em situações reais desde o dia 1.' },
  { icon: '👥', title: 'Turmas do Seu Nível Exato', text: 'Máximo de 5 pessoas, mesmo nível e perfil profissional. Ambiente seguro para errar, crescer e criar network global.' },
  { icon: '💼', title: 'Real-World Intelligence', text: 'Esqueça livros datados. Nosso material cobre Business, Tech e Cultura Global — curado por especialistas de Londres e Barcelona.' },
  { icon: '📊', title: 'Acompanhe Seu Progresso', text: 'Sua taxa de presença, conquistas e histórico estão todos aqui no portal. Consistência é o que separa quem aprende de quem se frustra.' },
];

const getWeekNumber = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
};

const getPaywallType = (lessonTitle, plan) => {
  if (!lessonTitle || !plan) return null;
  if ((PLAN_ACCESS[plan] || []).includes(lessonTitle)) return null;
  if (plan === 'individual_vip' && lessonTitle === 'Speaking Club') return 'sc_upsell';
  return 'plan_upgrade';
};

const calcStreaks = (attRecords) => {
  const present = attRecords.filter(a => a.status === 'present' && a.lessons?.class_time);
  if (!present.length) return { current: 0, max: 0 };
  const getMonday = (d) => {
    const dt = new Date(d); const day = dt.getUTCDay();
    dt.setUTCDate(dt.getUTCDate() - (day === 0 ? 6 : day - 1)); dt.setUTCHours(0,0,0,0);
    return dt.getTime();
  };
  const weeks = [...new Set(present.map(a => getMonday(a.lessons.class_time)))].sort((a,b) => b-a);
  const thisWeek = getMonday(new Date()); const lastWeek = thisWeek - 7*24*60*60*1000;
  let current = 0;
  if (weeks[0] === thisWeek || weeks[0] === lastWeek) {
    current = 1;
    for (let i = 1; i < weeks.length; i++) {
      if (weeks[i-1] - weeks[i] === 7*24*60*60*1000) current++;
      else break;
    }
  }
  let max = 1, temp = 1;
  for (let i = 1; i < weeks.length; i++) {
    if (weeks[i-1] - weeks[i] === 7*24*60*60*1000) { temp++; max = Math.max(max, temp); }
    else temp = 1;
  }
  return { current, max: Math.max(current, max) };
};

const appStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; }
  body { background: #f0f4f8; font-family: 'Plus Jakarta Sans', sans-serif; margin: 0; color: #0f172a; }
  .app-wrap { max-width: 960px; margin: 0 auto; padding: 24px 20px 60px; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.65); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 5000; animation: fadeIn 0.2s ease-out; }
  .modal-box { background: white; padding: 36px 28px; border-radius: 28px; width: 90%; max-width: 420px; text-align: center; box-shadow: 0 30px 60px rgba(0,0,0,0.2); animation: slideUp 0.3s cubic-bezier(0.16,1,0.3,1); position: relative; overflow-y: auto; max-height: 92vh; }
  .modal-wide { max-width: 460px; text-align: left; }
  .modal-close { position: absolute; top: 14px; right: 14px; background: #f1f5f9; border: none; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1rem; color: #64748b; z-index: 10; transition: background 0.15s; flex-shrink: 0; }
  .modal-close:hover { background: #e2e8f0; color: #0f172a; }

  .btn-main { width: 100%; padding: 15px; border: none; border-radius: 14px; font-weight: 800; font-size: 0.95rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; margin-top: 10px; transition: opacity 0.15s, transform 0.1s; color: white; }
  .btn-main:active { transform: scale(0.97); }
  .btn-cancel-vaga { background: #fff; color: #ef4444; border: 2px solid #ef4444; }
  .btn-ghost { background: none; border: none; color: #94a3b8; font-weight: 600; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.82rem; padding: 8px; width: 100%; margin-top: 4px; }

  .card-section { background: white; border-radius: 20px; padding: 24px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
  .sec-title { font-size: 0.68rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1.5px; margin: 0 0 14px; }

  .sotaq-ad { background: linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #1a2a6c 100%); border-radius: 16px; padding: 18px 22px; display: flex; align-items: center; gap: 16px; margin-bottom: 20px; box-shadow: 0 4px 20px rgba(15,23,42,0.2); }
  .sotaq-btn { background: linear-gradient(135deg, #ff6a00, #ff8c00); color: white; padding: 10px 20px; border-radius: 50px; font-weight: 800; font-size: 0.82rem; text-decoration: none; white-space: nowrap; transition: transform 0.15s; display: inline-block; }
  .sotaq-btn:hover { transform: translateY(-1px); }

  .next-banner { background: linear-gradient(135deg, #1a2a6c 0%, #1e40af 100%); border-radius: 20px; padding: 22px 28px; display: flex; align-items: center; justify-content: space-between; gap: 20px; box-shadow: 0 8px 30px rgba(30,58,138,0.28); flex-wrap: wrap; margin-bottom: 20px; }
  .join-btn { display: inline-flex; align-items: center; gap: 8px; background: #ff6a00; color: white; padding: 14px 26px; border-radius: 50px; font-weight: 800; font-size: 0.9rem; text-decoration: none; white-space: nowrap; box-shadow: 0 4px 15px rgba(255,106,0,0.4); transition: transform 0.15s; }
  .join-btn:hover { transform: translateY(-2px); }
  .join-btn-modal { display: flex; align-items: center; justify-content: center; gap: 8px; background: linear-gradient(135deg,#16a34a,#15803d); color: white; padding: 16px; border-radius: 14px; font-weight: 800; font-size: 1rem; text-decoration: none; width: 100%; box-sizing: border-box; margin-top: 12px; transition: opacity 0.15s; }
  .join-btn-modal:hover { opacity: 0.9; }

  .stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
  .stat-pill { background: #f8fafc; border-radius: 14px; padding: 14px 10px; text-align: center; }
  .stat-value { font-size: 1.5rem; font-weight: 900; line-height: 1; }
  .stat-label { font-size: 0.62rem; font-weight: 700; color: #94a3b8; margin-top: 5px; text-transform: uppercase; letter-spacing: 0.5px; line-height: 1.3; }

  .goal-card { background: linear-gradient(135deg, #f0f9ff, #e0f2fe); border: 1.5px solid #bae6fd; border-radius: 16px; padding: 16px 20px; margin-bottom: 20px; display: flex; align-items: center; gap: 14px; cursor: pointer; transition: box-shadow 0.2s; }
  .goal-card:hover { box-shadow: 0 4px 16px rgba(2,132,199,0.15); }
  .goal-empty { background: linear-gradient(135deg, #fafafa, #f1f5f9); border: 2px dashed #cbd5e1; border-radius: 16px; padding: 16px 20px; margin-bottom: 20px; display: flex; align-items: center; gap: 14px; cursor: pointer; transition: all 0.2s; }
  .goal-empty:hover { border-color: #94a3b8; background: #f8fafc; }

  @keyframes badgePop { 0% { transform: scale(0.8); opacity: 0; } 70% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
  @keyframes badgeShine { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
  .badges-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
  .badge-card { border-radius: 14px; padding: 14px 6px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 5px; position: relative; overflow: hidden; }
  .badge-card.earned { animation: badgePop 0.4s ease forwards; }
  .badge-card.locked { background: #f8fafc; border: 2px dashed #e2e8f0; opacity: 0.72; filter: grayscale(0.5); }
  .badge-icon { font-size: 1.8rem; line-height: 1; position: relative; z-index: 1; }
  .badge-label { font-size: 0.62rem; font-weight: 800; line-height: 1.3; position: relative; z-index: 1; }
  .badge-desc { font-size: 0.55rem; font-weight: 600; line-height: 1.3; position: relative; z-index: 1; display: none; }
  .badge-card.earned .badge-desc { display: block; color: rgba(255,255,255,0.85); }
  .badge-card.locked .badge-desc { display: block; color: #64748b; }
  .badge-card.earned .badge-label { color: white; }
  .badge-card.locked .badge-label { color: #475569; }
  .badge-shine { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 60%); animation: badgeShine 2s ease-in-out infinite; }
  .badge-next { margin-top: 14px; background: linear-gradient(135deg, #f0fdf4, #dcfce7); border-radius: 12px; padding: 12px 16px; display: flex; align-items: center; gap: 12px; border: 1.5px solid #86efac; }

  .reflection-card { background: linear-gradient(135deg, #fefce8, #fef9c3); border: 1.5px solid #fde047; border-radius: 20px; padding: 22px; margin-bottom: 20px; }
  .reflection-textarea { width: 100%; padding: 14px; border-radius: 12px; border: 1.5px solid #fde047; background: white; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.9rem; line-height: 1.6; resize: vertical; outline: none; color: #0f172a; box-sizing: border-box; }
  .reflection-textarea:focus { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.1); }

  .upcoming-item { display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: #f8fafc; border-radius: 12px; margin-bottom: 8px; border: 1.5px solid #f1f5f9; transition: border-color 0.15s; }
  .upcoming-item:hover { border-color: #e2e8f0; }

  .checkin-opt { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 14px; border: 2px solid #e2e8f0; cursor: pointer; font-weight: 700; font-size: 0.88rem; color: #475569; transition: all 0.15s; text-align: left; background: white; width: 100%; font-family: 'Plus Jakarta Sans', sans-serif; margin-bottom: 8px; }
  .checkin-opt:hover { border-color: #1a2a6c; color: #1a2a6c; background: #f0f4ff; }
  .checkin-opt.selected { border-color: #1a2a6c; background: #1a2a6c; color: white; }

  .att-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 10px; background: #f8fafc; margin-bottom: 6px; }
  .att-chip { padding: 4px 10px; border-radius: 6px; font-weight: 800; font-size: 0.7rem; white-space: nowrap; }

  .legend-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
  .leg-pill { background: white; border: 1.5px solid #f1f5f9; border-radius: 50px; padding: 7px 14px; font-size: 0.78rem; font-weight: 700; display: flex; align-items: center; gap: 7px; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
  .col-dot { width: 11px; height: 11px; border-radius: 50%; flex-shrink: 0; }

  .referral-wrap { background: linear-gradient(135deg, #ff6a00 0%, #ff8c00 100%); border-radius: 20px; padding: 20px 24px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; box-shadow: 0 6px 20px rgba(255,106,0,0.25); }
  .referral-btn { background: white; color: #ff6a00; padding: 12px 22px; border-radius: 50px; font-weight: 800; font-size: 0.85rem; text-decoration: none; white-space: nowrap; transition: transform 0.15s; display: inline-block; }
  .referral-btn:hover { transform: translateY(-1px); }

  .tips-wrap { background: white; border-radius: 20px; padding: 22px 24px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
  @keyframes tipFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .tip-card { animation: tipFade 0.4s ease; display: flex; gap: 16px; align-items: flex-start; }
  .tip-dots { display: flex; gap: 6px; justify-content: center; margin-top: 18px; }
  .tip-dot { width: 7px; height: 7px; border-radius: 50%; background: #e2e8f0; cursor: pointer; transition: background 0.2s, transform 0.2s; }
  .tip-dot.active { background: #1a2a6c; transform: scale(1.3); }

  .wa-btn { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; border-radius: 14px; font-weight: 800; font-size: 0.9rem; text-decoration: none; width: 100%; box-sizing: border-box; margin-top: 10px; background: #16a34a; color: white; transition: opacity 0.15s; }
  .wa-btn:hover { opacity: 0.9; }
  .perk { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; color: #475569; font-weight: 600; }
  .perk:last-child { border: none; }

  .fc { background: white; padding: 20px; border-radius: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.04); }
  .fc-theme-standard th { border: none !important; padding: 12px 0 !important; font-size: 0.78rem; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .fc-theme-standard td { border-color: #f1f5f9 !important; }
  .fc-scrollgrid { border: none !important; }
  .fc-event { border-radius: 7px !important; border: none !important; box-shadow: 0 2px 6px rgba(0,0,0,0.1); transition: all 0.2s !important; padding: 2px !important; }
  .fc-event:hover { transform: translateY(-2px) scale(1.02); box-shadow: 0 5px 14px rgba(0,0,0,0.15); z-index: 10 !important; }
  .fc-event-main { font-weight: 700 !important; font-size: 0.75rem; padding: 3px 5px; }
  .fc-toolbar-title { font-weight: 900 !important; color: #0f172a !important; font-size: 1.3rem !important; }
  .fc-button-primary { background: #1a2a6c !important; border: none !important; border-radius: 10px !important; font-weight: 800 !important; font-size: 0.78rem !important; padding: 9px 16px !important; text-transform: uppercase; }
  .fc-button-active { background: #ff6a00 !important; }
  .fc-day-today { background-color: #f0f9ff !important; }

  @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
  @keyframes slideUp { from { transform: translateY(60px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }

  .app-footer { border-top: 1px solid #e2e8f0; margin-top: 32px; padding-top: 24px; padding-bottom: 8px; text-align: center; }
  .app-footer-links { display: flex; justify-content: center; gap: 24px; margin-bottom: 12px; flex-wrap: wrap; }
  .app-footer-link { font-size: 0.82rem; font-weight: 700; color: #64748b; text-decoration: none; transition: color 0.2s; }
  .app-footer-link:hover { color: #1a2a6c; }

  .bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; background: white; border-top: 1.5px solid #f1f5f9; display: flex; z-index: 1000; box-shadow: 0 -4px 20px rgba(0,0,0,0.08); }
  .bottom-nav-btn { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; padding: 10px 4px 14px; border: none; background: none; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.15s; }
  .bottom-nav-btn .nav-icon { font-size: 1.3rem; line-height: 1; }
  .bottom-nav-btn .nav-label { font-size: 0.62rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
  .bottom-nav-btn.active .nav-icon { transform: translateY(-2px); }
  .bottom-nav-btn.active .nav-label { color: #1a2a6c; }
  .bottom-nav-btn:not(.active) .nav-label { color: #94a3b8; }

  @media (max-width: 768px) {
    .app-wrap { padding: 12px 12px 60px; }
    .fc { padding: 12px 8px; border-radius: 16px; }
    .fc-toolbar { flex-direction: column; gap: 12px; }
    .fc-toolbar-chunk { display: flex; justify-content: center; }
    .fc-button-primary { padding: 8px 12px !important; font-size: 0.72rem !important; }
    .modal-box { position: absolute; bottom: 0; border-radius: 28px 28px 0 0; padding-bottom: 48px; max-width: 100%; width: 100%; }
    .modal-wide { max-width: 100%; }
    .next-banner { flex-direction: column; align-items: flex-start; }
    .join-btn { width: 100%; justify-content: center; }
    .stats-row { grid-template-columns: repeat(2, 1fr); }
    .badges-grid { grid-template-columns: repeat(3, 1fr); }
    .sotaq-ad { flex-direction: column; align-items: flex-start; }
    .sotaq-btn { width: 100%; text-align: center; }
    .referral-wrap { flex-direction: column; }
    .referral-btn { width: 100%; text-align: center; }
  }
`;

export default function App() {
  const [session, setSession] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [status, setStatus] = useState('');
  const [nextClass, setNextClass] = useState(null);
  const [myAttendance, setMyAttendance] = useState([]);
  const [myCancellations, setMyCancellations] = useState([]);
  const [waitlistPositions, setWaitlistPositions] = useState({});
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [hasLevelUp, setHasLevelUp] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallType, setPaywallType] = useState(null);
  const [tipIdx, setTipIdx] = useState(0);
  const [lessonMaterials, setLessonMaterials] = useState([]);

  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [goalText, setGoalText] = useState('');
  const [goalSaving, setGoalSaving] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);

  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkinSelected, setCheckinSelected] = useState('');
  const [thisWeekCheckin, setThisWeekCheckin] = useState(null);
  const [checkinSaving, setCheckinSaving] = useState(false);

  const [reflectionText, setReflectionText] = useState('');
  const [thisWeekReflection, setThisWeekReflection] = useState(null);
  const [reflectionSaving, setReflectionSaving] = useState(false);
  const [reflectionSaved, setReflectionSaved] = useState(false);

  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [activeTab, setActiveTab] = useState('portal');
  const [gameCompletions, setGameCompletions] = useState([]);
  const [missionsTotal, setMissionsTotal] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTipIdx(i => (i + 1) % TIPS.length), 10000);
    return () => clearInterval(t);
  }, []);

  const loadAllData = useCallback(async (studentData) => {
    const weekNum = getWeekNumber();
    const yearNum = new Date().getFullYear();
    const clean = studentData.email.toLowerCase().trim();
    const userPlan = studentData.plan || 'elite_squad';
    const userLevel = studentData.level || 'Beginner';
    const userStartDate = studentData.start_date || studentData.created_at;
    const today = new Date(); today.setHours(0, 0, 0, 0);

    const [
      { data: lessons },
      { data: signups },
      { data: att },
      { data: canc },
      { data: checkins },
      { data: reflections },
      { data: waitlist },
      { data: levelProg },
      { data: materials },
      { data: gameComps },
      { data: missions },
    ] = await Promise.all([
      supabase.from('lessons').select('*').gte('class_time', today.toISOString()),
      supabase.from('signups').select('lesson_id, student_email'),
      supabase.from('attendance').select('*, lessons(title, class_time)').eq('student_email', clean),
      supabase.from('cancellations').select('*').eq('student_email', clean),
      supabase.from('checkins').select('*').eq('student_id', studentData.id).eq('week_number', weekNum).eq('year', yearNum),
      supabase.from('reflections').select('*').eq('student_id', studentData.id).eq('week_number', weekNum).eq('year', yearNum),
      supabase.from('waitlist').select('*').order('created_at', { ascending: true }),
      supabase.from('level_progression').select('id').eq('student_id', studentData.id).limit(1),
      supabase.from('lesson_materials').select('*'),
      supabase.from('game_completions').select('played_date, game_type').eq('student_id', studentData.id).order('played_date', { ascending: false }),
      supabase.from('mission_completions').select('id').eq('student_id', studentData.id),
    ]);

    const attData = att || [];
    const cancData = canc || [];
    const checkinData = checkins && checkins.length > 0 ? checkins[0] : null;
    const reflectionData = reflections && reflections.length > 0 ? reflections[0] : null;

    setMyAttendance(attData);
    setMyCancellations(cancData);
    setThisWeekCheckin(checkinData);
    setThisWeekReflection(reflectionData);
    setLessonMaterials(materials || []);
    setHasLevelUp(!!(levelProg && levelProg.length > 0));
    setGameCompletions(gameComps || []);
    setMissionsTotal((missions || []).length);
    if (reflectionData) { setReflectionText(reflectionData.response || ''); setReflectionSaved(true); }

    const positions = {};
    if (waitlist) {
      const myWl = waitlist.filter(w => w.student_email === clean);
      myWl.forEach(w => {
        const pos = waitlist.filter(ww => ww.lesson_id === w.lesson_id).findIndex(ww => ww.student_email === clean) + 1;
        positions[w.lesson_id] = pos;
      });
    }
    setWaitlistPositions(positions);

    if (!checkinData) setTimeout(() => setCheckinOpen(true), 800);
    if (!studentData.personal_goal) setTimeout(() => setGoalModalOpen(true), checkinData ? 500 : 2800);

    if (!lessons) return;

    const userAllowed = userLevel === 'Beginner' ? ['Beginner', 'Speaking'] : userLevel === 'Intermediate' ? ['Intermediate', 'Speaking'] : ['Speaking'];

    const filtered = lessons.filter(l => {
      if (l.title === 'Individual lesson' || l.title === 'Elite Squad Individual') {
        return (PLAN_ACCESS[userPlan] || []).includes(l.title);
      }
      if (!userAllowed.includes(l.level) && l.title !== 'Plantão de dúvidas' && l.title !== 'Elite Squad Individual' && l.title !== 'Individual lesson') return false;
      return true;
    });

    const enrolledFuture = filtered
      .filter(l => (signups || []).some(s => s.lesson_id === l.id && s.student_email === clean))
      .sort((a, b) => new Date(a.class_time) - new Date(b.class_time));
    setUpcomingLessons(enrolledFuture);
    setNextClass(enrolledFuture.find(l => l.meet_link) || null);

    const formattedEvents = filtered.map(l => {
      const paywall = getPaywallType(l.title, userPlan);
      const lSignups = (signups || []).filter(s => s.lesson_id === l.id);
      let cap = 6;
      if (l.title === 'Individual lesson' || l.title === 'Elite Squad Individual') cap = 1;
      else if (l.title === 'Plantão de dúvidas') cap = 9999;
      const isFull = lSignups.length >= cap;
      const isEnrolled = lSignups.some(s => s.student_email === clean);
      const isLocked = !!paywall;

      let color = '#1e3a8a';
      if (l.title === 'Elite Squad') color = '#7e22ce';
      else if (l.title === 'Speaking Club') color = '#0d9488';
      else if (l.title === 'Plantão de dúvidas') color = '#0284c7';
      else if (l.title === 'Elite Squad Individual') color = '#d97706';
      if (isEnrolled) color = '#16a34a';
      else if (isFull && !isLocked) color = '#dc2626';
      else if (isLocked) color = '#94a3b8';

      const prefix = isEnrolled ? '✅ ' : isFull && !isLocked ? '🚫 ' : isLocked ? '🔒 ' : '';

      return {
        id: l.id,
        title: `${prefix}${l.title}${l.teacher_name ? ` (${l.teacher_name})` : ''}`,
        start: l.class_time,
        end: new Date(new Date(l.class_time).getTime() + 3600000).toISOString(),
        backgroundColor: color, borderColor: color,
        extendedProps: { isFull, isEnrolled, isLocked, paywall, time: l.class_time, meet_link: l.meet_link || null, rawTitle: l.title },
      };
    });
    setEvents(formattedEvents);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    const clean = loginEmail.toLowerCase().trim();
    const { data } = await supabase.from('students').select('*').eq('email', clean).single();
    if (data) {
      if (data.contract_status === 'cancelled') {
        alert('Seu contrato foi encerrado. Entre em contato com a equipe para mais informações.');
        return;
      }
      setSession(data);
      localStorage.setItem('quevedo_session', JSON.stringify(data));
      loadAllData(data);
    } else {
      alert('E-mail não encontrado. Verifique se digitou corretamente.');
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem('quevedo_session');
    if (!saved) return;
    const userData = JSON.parse(saved);
    supabase.from('students').select('*').eq('email', userData.email).single().then(({ data }) => {
      if (!data) {
        localStorage.clear();
        return;
      }
      setSession(data);
      localStorage.setItem('quevedo_session', JSON.stringify(data));
      loadAllData(data);
    });
  }, [loadAllData]);

  const initCancel = () => { setCancelReason(''); setCancelModalOpen(true); };

  const confirmCancel = async () => {
    setStatus('loading');
    const clean = session.email.toLowerCase().trim();
    await supabase.from('cancellations').insert({ student_email: clean, lesson_id: selectedLesson.id, reason: cancelReason || null });
    const { error } = await supabase.from('signups').delete().match({ student_email: clean, lesson_id: selectedLesson.id });
    if (!error) { setCancelModalOpen(false); setStatus('cancelled'); setTimeout(() => window.location.reload(), 1500); }
    else setStatus('error');
  };

  const handleAction = async () => {
    setStatus('loading');
    const clean = session.email.toLowerCase().trim();
    if (selectedLesson.extendedProps.isFull) {
      const { error } = await supabase.from('waitlist').insert([{ lesson_id: selectedLesson.id, student_email: clean }]);
      if (!error) { setStatus('waitlist'); setTimeout(() => setSelectedLesson(null), 2000); }
      else setStatus('error');
    } else {
      try {
        const { data, error } = await supabase.rpc('join_class', { target_lesson_id: selectedLesson.id, s_name: session.full_name, s_email: clean });
        if (error) throw error;
        if (data === 'Success') {
          emailjs.send(process.env.REACT_APP_EMAILJS_SERVICE_ID, process.env.REACT_APP_EMAILJS_TEMPLATE_ID, {
            to_email: clean, lesson_title: selectedLesson.extendedProps.rawTitle,
            lesson_time: new Date(selectedLesson.extendedProps.time).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }),
            meet_link: selectedLesson.extendedProps.meet_link || '',
          }, process.env.REACT_APP_EMAILJS_PUBLIC_KEY).catch(() => {});
          setStatus('success'); setTimeout(() => window.location.reload(), 1500);
        } else {
          setStatus(data === 'Full' ? 'full' : data === 'Already Signed Up' ? 'already' : 'error');
        }
      } catch { setStatus('error'); }
    }
  };

  const saveGoal = async () => {
    if (!goalText.trim()) return;
    setGoalSaving(true);
    await supabase.from('students').update({ personal_goal: goalText.trim(), goal_set_at: new Date().toISOString() }).eq('id', session.id);
    const updated = { ...session, personal_goal: goalText.trim() };
    setSession(updated);
    localStorage.setItem('quevedo_session', JSON.stringify(updated));
    setGoalSaving(false); setGoalModalOpen(false); setEditingGoal(false);
  };

  const saveCheckin = async (answer) => {
    setCheckinSelected(answer);
    setCheckinSaving(true);
    const weekNum = getWeekNumber(); const yearNum = new Date().getFullYear();
    const q = CHECKIN_QUESTIONS[weekNum % CHECKIN_QUESTIONS.length];
    await supabase.from('checkins').upsert({ student_id: session.id, question: q.q, answer, week_number: weekNum, year: yearNum }, { onConflict: 'student_id,week_number,year' });
    setThisWeekCheckin({ question: q.q, answer });
    setCheckinSaving(false);
    setTimeout(() => setCheckinOpen(false), 1000);
  };

  const saveReflection = async () => {
    if (!reflectionText.trim()) return;
    setReflectionSaving(true);
    const weekNum = getWeekNumber(); const yearNum = new Date().getFullYear();
    const levelPrompts = REFLECTION_PROMPTS[session?.level] || REFLECTION_PROMPTS['Beginner'];
    const prompt = levelPrompts[weekNum % levelPrompts.length];
    await supabase.from('reflections').upsert({ student_id: session.id, prompt, response: reflectionText.trim(), week_number: weekNum, year: yearNum }, { onConflict: 'student_id,week_number,year' });
    setThisWeekReflection({ prompt, response: reflectionText.trim() });
    setReflectionSaving(false); setReflectionSaved(true);
  };

  const userPlan = session?.plan || 'elite_squad';
  const totalAtt = myAttendance.filter(a => a.status === 'present').length;
  const totalMarked = myAttendance.length;
  const attRate = totalMarked > 0 ? Math.round((totalAtt / totalMarked) * 100) : null;
  const { current: streak, max: maxStreak } = calcStreaks(myAttendance);
  const scAtt = myAttendance.filter(a => a.status === 'present' && a.lessons?.title === 'Speaking Club').length;
  const totalCancellations = myCancellations.length;
  const eliteCompleted = session?.elite_completed || false;

  const firstAttClass = [...myAttendance].sort((a, b) => new Date(a.lessons?.class_time) - new Date(b.lessons?.class_time))[0];
  const decolagem = !!(firstAttClass?.lessons?.class_time && session?.created_at &&
    new Date(firstAttClass.lessons.class_time) - new Date(session.created_at) <= 48 * 60 * 60 * 1000);

  const badgeData = { totalAtt, maxStreak, scAtt, totalCancellations, hasLevelUp, eliteCompleted, decolagem,
    gamesTotal: gameCompletions.length,
    gameStreak: (() => {
      const dates = [...new Set(gameCompletions.map(c => c.played_date))].sort().reverse();
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      if (!dates.length || (dates[0] !== today && dates[0] !== yesterday)) return 0;
      let s = 1;
      for (let i = 1; i < dates.length; i++) {
        if ((new Date(dates[i-1]) - new Date(dates[i])) / 86400000 === 1) s++; else break;
      }
      return s;
    })(),
    gameTypesPlayed: new Set(gameCompletions.map(c => c.game_type)).size,
    missionsTotal,
  };
  const earnedBadges = BADGES.filter(b => b.check(badgeData));
  const nextBadge = BADGES.find(b => !b.check(badgeData));

  const weekNum = getWeekNumber();
  const levelPrompts = REFLECTION_PROMPTS[session?.level] || REFLECTION_PROMPTS['Beginner'];
  const thisWeekPrompt = levelPrompts[weekNum % levelPrompts.length];
  const thisWeekCheckinQ = CHECKIN_QUESTIONS[weekNum % CHECKIN_QUESTIONS.length];
  const levelColor = { Beginner: '#1d4ed8', Intermediate: '#7c3aed', Speaking: '#16a34a' };
  const isMobile = window.innerWidth <= 768;
  const referralMsg = encodeURIComponent(`Oi! Quero indicar um amigo para a Student Portal. Meu nome é ${session?.full_name || ''}. Como funciona o desconto de indicação?`);
  const tip = TIPS[tipIdx];

  // ── LOGIN SCREEN ────────────────────────────────────────────────────────────
  if (!session) return (
    <>
      <style>{appStyles}</style>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg, #f0f4f8 0%, #e2e8f0 100%)' }}>
        <div style={{ background: 'white', padding: '52px 36px', borderRadius: '28px', boxShadow: '0 20px 50px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: '400px', width: '90%' }}>
          <div style={{ background: 'linear-gradient(135deg, #1a2a6c, #ff6a00)', color: 'white', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '18px', margin: '0 auto 28px', fontWeight: '900', fontSize: '2rem', boxShadow: '0 10px 24px rgba(255,106,0,0.25)' }}>Q</div>
          <h2 style={{ fontWeight: '900', color: '#0f172a', marginBottom: '2px', fontSize: '2rem' }}>Student Portal</h2>
          <p style={{ fontWeight: '800', color: '#ff6a00', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 8px' }}>Idiomas Quevedo</p>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '32px' }}>Please enter your email.</p>
          <form onSubmit={handleLogin}>
            <input type="email" placeholder="youremail@email.com" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} style={{ width: '100%', padding: '15px 18px', borderRadius: '14px', border: '1.5px solid #e2e8f0', marginBottom: '16px', textAlign: 'center', outline: 'none', fontSize: '0.95rem', background: '#f8fafc', fontFamily: 'Plus Jakarta Sans', boxSizing: 'border-box' }} required />
            <button type="submit" style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', boxShadow: '0 4px 15px rgba(30,58,138,0.25)' }}>ENTER</button>
          </form>
        </div>
      </div>
    </>
  );

  // Route to Spanish portal
  if (session.language === 'spanish') {
    return <AppES session={session} setSession={setSession} />;
  }

  return (
    <>
      <style>{appStyles}</style>
      <div className="app-wrap">

        {/* Bottom Navigation */}
        <nav className="bottom-nav">
          {[['portal','📅','Portal'],['practice','🎮','Praticar']].map(([key, icon, label]) => (
            <button key={key} className={`bottom-nav-btn${activeTab === key ? ' active' : ''}`} onClick={() => setActiveTab(key)}>
              <span className="nav-icon">{icon}</span>
              <span className="nav-label" style={{ color: activeTab === key ? '#1a2a6c' : '#94a3b8' }}>{label}</span>
            </button>
          ))}
        </nav>

        {/* Practice Tab */}
        {activeTab === 'practice' && <Practice session={session} onBack={() => setActiveTab('portal')} />}

        {/* Portal Tab */}
        {activeTab === 'portal' && <>

        {/* Brand header */}
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1a2a6c, #ff6a00)', color: 'white', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', fontWeight: '900', fontSize: '1.4rem', flexShrink: 0, boxShadow: '0 4px 12px rgba(255,106,0,0.25)' }}>Q</div>
            <div>
              <div style={{ fontWeight: '900', color: '#0f172a', fontSize: '1rem', lineHeight: 1.1 }}>Idiomas Quevedo</div>
              <div style={{ fontWeight: '700', color: '#94a3b8', fontSize: '0.7rem', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Portal do Aluno</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.9rem', lineHeight: 1.1 }}>{session.full_name.split(' ')[0]}</div>
              <button onClick={() => { localStorage.clear(); window.location.reload(); }} style={{ background: 'none', border: 'none', color: '#94a3b8', fontWeight: '600', cursor: 'pointer', fontSize: '0.72rem', padding: 0, fontFamily: 'Plus Jakarta Sans', textDecoration: 'underline' }}>Sair</button>
            </div>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: '900', flexShrink: 0, boxShadow: '0 3px 10px rgba(30,58,138,0.2)' }}>
              {session.full_name.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* User badges row */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
          <span style={{ padding: '6px 16px', borderRadius: '50px', background: levelColor[session.level] || '#64748b', color: 'white', fontWeight: '800', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{session.level}</span>
          <span style={{ padding: '6px 14px', borderRadius: '50px', background: (PLAN_BADGE[userPlan] || {}).bg, color: (PLAN_BADGE[userPlan] || {}).color, fontWeight: '800', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{PLAN_LABELS[userPlan]}</span>
          <button onClick={() => setUpcomingOpen(true)} style={{ padding: '6px 14px', borderRadius: '50px', background: '#f1f5f9', color: '#1a2a6c', fontWeight: '800', fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>
            📅 {upcomingLessons.length} aula{upcomingLessons.length !== 1 ? 's' : ''} agendada{upcomingLessons.length !== 1 ? 's' : ''}
          </button>
        </div>

        {/* ── NEXT CLASS BANNER ── */}
        {nextClass && (
          <div className="next-banner" style={{ marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '0.68rem', fontWeight: '800', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '5px' }}>🎯 Sua próxima aula</div>
              <div style={{ fontSize: '1.05rem', fontWeight: '800', color: 'white', marginBottom: '3px' }}>{nextClass.title}{nextClass.teacher_name ? ` · ${nextClass.teacher_name}` : ''}</div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'rgba(255,255,255,0.7)' }}>
                {new Date(nextClass.class_time).toLocaleString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <a href={nextClass.meet_link} target="_blank" rel="noopener noreferrer" className="join-btn">🎥 ENTRAR NA AULA</a>
          </div>
        )}

        {/* ── GAME RECOMMENDATION ── */}
        {!gameCompletions.some(c => c.played_date === new Date().toISOString().split('T')[0] && c.game_type === ['flashcard','complete','wordbuilder','oddout','phrasal'][new Date().getDay() % 5]) && (
          <button onClick={() => setActiveTab('practice')} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', padding: '16px 20px', borderRadius: '16px', border: 'none', marginBottom: '16px', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', boxShadow: '0 4px 16px rgba(124,58,237,0.3)', textAlign: 'left' }}>
            <span style={{ fontSize: '2rem', flexShrink: 0 }}>🎮</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '900', fontSize: '0.95rem', marginBottom: '2px' }}>Jogo do dia disponível!</div>
              <div style={{ fontWeight: '600', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)' }}>5 minutinhos de prática fazem diferença.</div>
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: '800', flexShrink: 0 }}>Jogar →</span>
          </button>
        )}

        {/* Personal goal */}
        {session.personal_goal ? (
          <div className="goal-card" onClick={() => { setGoalText(session.personal_goal); setEditingGoal(true); setGoalModalOpen(true); }}>
            <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>🎯</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '4px' }}>Meu Objetivo</div>
              <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.9rem', lineHeight: 1.4 }}>{session.personal_goal}</div>
            </div>
            <span style={{ fontSize: '0.72rem', color: '#0284c7', fontWeight: '700', flexShrink: 0 }}>Editar ✏️</span>
          </div>
        ) : (
          <div className="goal-empty" onClick={() => setGoalModalOpen(true)}>
            <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>🎯</span>
            <div>
              <div style={{ fontWeight: '800', color: '#64748b', fontSize: '0.9rem' }}>Defina seu objetivo com o inglês</div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600', marginTop: '3px' }}>Isso nos ajuda a personalizar sua jornada</div>
            </div>
            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '700', flexShrink: 0, marginLeft: 'auto' }}>Definir →</span>
          </div>
        )}

        {/* Check-in result badge */}
        {thisWeekCheckin && (
          <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1.5px solid #86efac', borderRadius: '14px', padding: '12px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.3rem' }}>✅</span>
            <div>
              <div style={{ fontWeight: '800', color: '#166534', fontSize: '0.82rem' }}>Check-in desta semana ✓</div>
              <div style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: '2px', fontWeight: '600' }}>"{thisWeekCheckin.answer}"</div>
            </div>
          </div>
        )}

        {/* Progress + Badges */}
        <div className="card-section">
          {totalMarked > 0 && (
            <div className="stats-row">
              <div className="stat-pill">
                <div className="stat-value" style={{ color: '#1a2a6c' }}>{totalAtt}</div>
                <div className="stat-label">Aulas Assistidas</div>
              </div>
              <div className="stat-pill">
                <div className="stat-value" style={{ color: attRate >= 80 ? '#16a34a' : attRate >= 60 ? '#d97706' : '#dc2626' }}>{attRate}%</div>
                <div className="stat-label">Presença</div>
              </div>
              <div className="stat-pill">
                <div className="stat-value" style={{ color: streak >= 3 ? '#ef4444' : '#64748b' }}>{streak}🔥</div>
                <div className="stat-label">Semanas Seguidas</div>
              </div>
              {userPlan === 'elite_squad' && (
                <div className="stat-pill">
                  <div className="stat-value" style={{ color: '#0d9488' }}>{scAtt}</div>
                  <div className="stat-label">Speaking Club</div>
                </div>
              )}
            </div>
          )}

          {userPlan === 'elite_squad' && scAtt === 0 && totalAtt > 0 && (
            <div style={{ background: 'linear-gradient(135deg, #f0fdfa, #ccfbf1)', border: '1.5px solid #99f6e4', borderRadius: '12px', padding: '14px 16px', marginBottom: '18px', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ fontSize: '1.4rem' }}>🗣️</span>
              <div>
                <div style={{ fontWeight: '800', color: '#0f766e', fontSize: '0.88rem' }}>O Speaking Club está incluído no seu plano!</div>
                <div style={{ color: '#0d9488', fontSize: '0.78rem', fontWeight: '600', marginTop: '2px' }}>Reserve uma sessão e acelere sua fluência com conversação ao vivo.</div>
              </div>
            </div>
          )}
          {userPlan === 'elite_squad' && scAtt > 0 && scAtt < 3 && (
            <div style={{ background: 'linear-gradient(135deg, #f0fdfa, #ccfbf1)', border: '1.5px solid #99f6e4', borderRadius: '12px', padding: '14px 16px', marginBottom: '18px', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ fontSize: '1.4rem' }}>💪</span>
              <div>
                <div style={{ fontWeight: '800', color: '#0f766e', fontSize: '0.88rem' }}>Você foi ao Speaking Club {scAtt}x — continue!</div>
                <div style={{ color: '#0d9488', fontSize: '0.78rem', fontWeight: '600', marginTop: '2px' }}>Alunos regulares progridem até 2x mais rápido.</div>
              </div>
            </div>
          )}

          <p className="sec-title">🏅 Suas Conquistas</p>
          <div className="badges-grid">
            {BADGES.map(b => {
              const earned = b.check(badgeData);
              return (
                <div key={b.id} className={`badge-card ${earned ? 'earned' : 'locked'}`}
                  style={earned ? { background: `linear-gradient(135deg, ${b.color}dd, ${b.color})` } : {}}
                  title={earned ? b.desc : b.hint}>
                  {earned && <div className="badge-shine" />}
                  <span className="badge-icon">{b.icon}</span>
                  <span className="badge-label">{b.label}</span>
                  <span className="badge-desc">{earned ? b.desc : b.hint}</span>
                </div>
              );
            })}
          </div>

          {nextBadge && (
            <div className="badge-next">
              <span style={{ fontSize: '1.4rem' }}>{nextBadge.icon}</span>
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#166534' }}>Próxima: <strong>{nextBadge.label}</strong></div>
                <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: '600', marginTop: '2px' }}>{nextBadge.hint}</div>
              </div>
            </div>
          )}
          {earnedBadges.length === BADGES.length && (
            <div style={{ marginTop: '14px', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', borderRadius: '12px', padding: '14px 16px', textAlign: 'center' }}>
              <span style={{ color: 'white', fontWeight: '800', fontSize: '0.9rem' }}>👑 Você desbloqueou todas as conquistas! Lenda!</span>
            </div>
          )}

          {myAttendance.length > 0 && (
            <>
              <p className="sec-title" style={{ marginTop: '22px' }}>📋 Histórico de Presença</p>
              <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                {[...myAttendance].sort((a, b) => new Date(b.lessons?.class_time) - new Date(a.lessons?.class_time)).slice(0, 10).map(rec => (
                  <div key={rec.id} className="att-row">
                    <span className="att-chip" style={{ background: rec.status === 'present' ? '#dcfce7' : rec.status === 'late' ? '#fef3c7' : '#fee2e2', color: rec.status === 'present' ? '#166534' : rec.status === 'late' ? '#92400e' : '#991b1b' }}>
                      {rec.status === 'present' ? 'Presente' : rec.status === 'late' ? 'Atrasado' : 'Faltou'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.lessons?.title || '—'}</div>
                      {rec.note && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>{rec.note}</div>}
                        {(() => {
                        const mat = (lessonMaterials || []).find(m => m.lesson_id === rec.lesson_id);
                        if (!mat) return null;
                        return (
                          <div style={{ marginTop: '6px', background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', fontSize: '0.72rem' }}>
                            {mat.content_covered && <div><span style={{ fontWeight: '700', color: '#0284c7' }}>📚 Conteúdo:</span> <span style={{ color: '#475569' }}>{mat.content_covered}</span></div>}
                            {mat.homework && <div style={{ marginTop: '3px' }}><span style={{ fontWeight: '700', color: '#d97706' }}>📝 Tarefa:</span> <span style={{ color: '#475569' }}>{mat.homework}</span></div>}
                          </div>
                        );
                      })()}
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: '0.72rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {rec.lessons?.class_time ? new Date(rec.lessons.class_time).toLocaleDateString('pt-BR', {}) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
          {totalMarked === 0 && (
            <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>Comece a assistir aulas para desbloquear suas conquistas! 🚀</p>
            </div>
          )}
        </div>

        {/* Reflection card */}
        <div className="reflection-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <span style={{ fontSize: '1.3rem' }}>✍️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '800', color: '#713f12', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Reflexão da Semana</div>
              <div style={{ fontSize: '0.68rem', color: '#92400e', fontWeight: '600', marginTop: '2px' }}>Sua resposta vai para o seu professor</div>
            </div>
            {(thisWeekReflection?.response || reflectionSaved) && (
              <span style={{ background: '#fde047', borderRadius: '50px', padding: '3px 10px', fontSize: '0.65rem', fontWeight: '800', color: '#713f12', flexShrink: 0 }}>✓ Enviada</span>
            )}
          </div>
          <div style={{ fontWeight: '700', color: '#713f12', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '14px', fontStyle: 'italic' }}>
            💬 {thisWeekPrompt}
          </div>
          <textarea
            className="reflection-textarea"
            rows={4}
            placeholder="Write your answer in English... don't worry about perfection, the important thing is to practice!"
            value={reflectionText}
            onChange={e => { setReflectionText(e.target.value); setReflectionSaved(false); }}
          />
          <button
            onClick={saveReflection}
            disabled={reflectionSaving || !reflectionText.trim()}
            style={{ marginTop: '12px', padding: '11px 22px', background: reflectionText.trim() ? 'linear-gradient(135deg, #d97706, #b45309)' : '#f1f5f9', color: reflectionText.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: reflectionText.trim() ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans', fontSize: '0.85rem', transition: 'all 0.2s' }}>
            {reflectionSaving ? 'Enviando...' : reflectionSaved ? '✓ Reflexão Enviada' : '📤 Enviar para o Professor'}
          </button>
        </div>

        {/* SotaQ ad */}
        <div className="sotaq-ad">
          <span style={{ fontSize: '2.2rem', flexShrink: 0 }}>🎙️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '800', color: 'white', fontSize: '0.95rem', marginBottom: '3px' }}>SotaQ — Laboratório de Pronúncia com IA</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', fontWeight: '600' }}>Treine seu sotaque 24h por dia. Tecnologia exclusiva Student Portal, 100% grátis para alunos.</div>
          </div>
          <a href="https://sotaq.netlify.app/" target="_blank" rel="noopener noreferrer" className="sotaq-btn">Testar Grátis →</a>
        </div>

        {/* Tips */}
        <div className="tips-wrap">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <p className="sec-title" style={{ margin: 0 }}>💡 Você sabia?</p>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => setTipIdx(i => (i - 1 + TIPS.length) % TIPS.length)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1.5px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#64748b', fontFamily: 'Plus Jakarta Sans' }}>‹</button>
              <button onClick={() => setTipIdx(i => (i + 1) % TIPS.length)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1.5px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#64748b', fontFamily: 'Plus Jakarta Sans' }}>›</button>
            </div>
          </div>
          <div className="tip-card" key={tipIdx}>
            <span style={{ fontSize: '2rem', flexShrink: 0, lineHeight: 1 }}>{tip.icon}</span>
            <div>
              <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.95rem', marginBottom: '4px' }}>{tip.title}</div>
              <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '600', lineHeight: 1.5 }}>{tip.text}</div>
              {tip.cta && tip.link && (
                <a href={tip.link} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: '10px', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', color: 'white', padding: '8px 18px', borderRadius: '50px', fontSize: '0.78rem', fontWeight: '800', textDecoration: 'none' }}>{tip.cta}</a>
              )}
            </div>
          </div>
          <div className="tip-dots">
            {TIPS.map((_, i) => <div key={i} className={`tip-dot ${i === tipIdx ? 'active' : ''}`} onClick={() => setTipIdx(i)} />)}
          </div>
        </div>

        {/* Referral */}
        <div className="referral-wrap">
          <div>
            <h3 style={{ margin: '0 0 4px', fontWeight: '900', color: 'white', fontSize: '1rem' }}>🎁 Indique um amigo!</h3>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', fontWeight: '600' }}>Seu amigo entra, você ganha um mês com desconto.</p>
          </div>
          <a href={`${WA}${referralMsg}`} target="_blank" rel="noopener noreferrer" className="referral-btn">💬 Indicar pelo WhatsApp</a>
        </div>

        {/* ── SCHEDULE CTA ── */}
        <a href="#cal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', color: 'white', padding: '16px 24px', borderRadius: '16px', fontWeight: '800', fontSize: '1rem', textDecoration: 'none', marginBottom: '16px', boxShadow: '0 4px 16px rgba(30,58,138,0.25)', transition: 'opacity 0.15s' }}>
          📅 Ver calendário e agendar uma aula
        </a>

        {/* Legend */}
        <div className="legend-row">
          {[['#1e3a8a','Individual'],['#d97706','Elite Ind.'],['#7e22ce','Elite Squad'],['#0d9488','Speaking Club'],['#0284c7','Plantão'],['#16a34a','✅ Inscrito'],['#dc2626','🚫 Lotada'],['#94a3b8','🔒 Upgrade']].map(([c,l]) => (
            <div key={l} className="leg-pill"><span className="col-dot" style={{ background: c }} />{l}</div>
          ))}
        </div>

        {/* ── CALENDAR ── */}
        <div id="cal">
          <FullCalendar
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
            timeZone="local" locale={ptBrLocale} firstDay={1} hiddenDays={[0, 6]}
            allDaySlot={false} slotMinTime="07:00:00" slotMaxTime="22:00:00" nowIndicator
            dayHeaderFormat={{ weekday: 'short', day: '2-digit', omitCommas: true }}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', meridiem: false }}
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', omitZeroMinute: false, meridiem: false }}
            events={events}
            eventClick={info => {
              const ep = info.event.extendedProps;
              if (ep.isLocked || ep.paywall) { setPaywallType(ep.paywall || 'plan_upgrade'); setPaywallOpen(true); return; }
              setStatus(''); setSelectedLesson({ id: info.event.id, title: info.event.title, extendedProps: ep });
            }}
            height="auto"
          />
        </div>

        {/* ── FOOTER ── */}
        <footer className="app-footer">
          <div className="app-footer-links">
            {[
              { label: '🌐 Site', href: 'https://idiomasquevedo.netlify.app/' },
              { label: '📸 Instagram', href: 'https://www.instagram.com/idiomasquevedo/' },
              { label: '🎙️ SotaQ IA', href: 'https://sotaq.netlify.app/' },
              { label: '💬 WhatsApp', href: 'https://api.whatsapp.com/send?phone=553198011835' },
            ].map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="app-footer-link">{label}</a>
            ))}
          </div>
          <p style={{ fontSize: '0.72rem', color: '#cbd5e1', margin: 0 }}>© 2026 ZonaQ · Todos os direitos reservados</p>
        </footer>

        {/* ── MODALS ─────────────────────────────────────────────────── */}

        {/* Weekly Check-in */}
        {checkinOpen && !thisWeekCheckin && (
          <div className="modal-overlay">
            <div className="modal-box" style={{ maxWidth: '440px' }}>
              <button className="modal-close" onClick={() => setCheckinOpen(false)}>✕</button>
              <div style={{ marginBottom: '6px' }}>
                <span style={{ background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', color: 'white', padding: '4px 12px', borderRadius: '50px', fontSize: '0.68rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>Check-in da Semana</span>
              </div>
              <div style={{ width: '44px', height: '5px', background: '#e2e8f0', borderRadius: '10px', margin: '16px auto 20px' }} />
              <p style={{ fontWeight: '800', fontSize: '1.05rem', color: '#0f172a', margin: '0 0 20px', lineHeight: 1.4 }}>{thisWeekCheckinQ.q}</p>
              <div style={{ textAlign: 'left' }}>
                {thisWeekCheckinQ.opts.map(opt => (
                  <button key={opt} className={`checkin-opt ${checkinSelected === opt ? 'selected' : ''}`}
                    onClick={() => saveCheckin(opt)} disabled={checkinSaving}>
                    {checkinSaving && checkinSelected === opt ? '⏳ Salvando...' : opt}
                  </button>
                ))}
              </div>
              <button className="btn-ghost" onClick={() => setCheckinOpen(false)}>Responder depois</button>
            </div>
          </div>
        )}

        {/* Goal modal */}
        {goalModalOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setGoalModalOpen(false)}>
            <div className="modal-box" style={{ maxWidth: '440px', textAlign: 'left' }}>
              <button className="modal-close" onClick={() => { setGoalModalOpen(false); setEditingGoal(false); }}>✕</button>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '2.5rem' }}>🎯</span>
                <h2 style={{ margin: '10px 0 4px', fontWeight: '900', color: '#0f172a', fontSize: '1.3rem' }}>
                  {editingGoal ? 'Atualizar meu objetivo' : 'Qual é o seu objetivo?'}
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>
                  {editingGoal ? 'Objetivos evoluem — atualize quando quiser.' : 'Escreva em português ou inglês. Seja específico!'}
                </p>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Exemplos de objetivos:</div>
                {['Conseguir uma promoção que exige inglês fluente', 'Me comunicar em reuniões internacionais sem travar', 'Morar fora do Brasil nos próximos 2 anos', 'Fazer networking global na minha área'].map(ex => (
                  <div key={ex} onClick={() => setGoalText(ex)}
                    style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', marginBottom: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', color: '#475569', border: goalText === ex ? '2px solid #1a2a6c' : '2px solid transparent', transition: 'all 0.15s' }}>
                    {ex}
                  </div>
                ))}
              </div>
              <textarea
                rows={3}
                placeholder="Ou escreva o seu próprio objetivo aqui..."
                value={goalText}
                onChange={e => setGoalText(e.target.value)}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontFamily: 'Plus Jakarta Sans', fontSize: '0.9rem', lineHeight: 1.5, resize: 'none', boxSizing: 'border-box', marginTop: '8px' }}
              />
              <button onClick={saveGoal} disabled={goalSaving || !goalText.trim()}
                style={{ width: '100%', padding: '14px', marginTop: '12px', background: goalText.trim() ? 'linear-gradient(135deg, #1a2a6c, #2563eb)' : '#f1f5f9', color: goalText.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: goalText.trim() ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans', transition: 'all 0.2s' }}>
                {goalSaving ? 'Salvando...' : editingGoal ? '✓ Atualizar Objetivo' : '🎯 Definir Meu Objetivo'}
              </button>
              {!editingGoal && <button className="btn-ghost" onClick={() => setGoalModalOpen(false)}>Definir mais tarde</button>}
            </div>
          </div>
        )}

        {/* Upcoming classes modal */}
        {upcomingOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setUpcomingOpen(false)}>
            <div className="modal-box modal-wide" style={{ maxWidth: '480px', textAlign: 'left' }}>
              <button className="modal-close" onClick={() => setUpcomingOpen(false)}>✕</button>
              <h2 style={{ margin: '0 0 4px', fontWeight: '900', color: '#0f172a', paddingRight: '40px' }}>📅 Minhas Aulas</h2>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '20px' }}>{upcomingLessons.length} aula{upcomingLessons.length !== 1 ? 's' : ''} agendada{upcomingLessons.length !== 1 ? 's' : ''}</p>
              {upcomingLessons.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📭</div>
                  <div style={{ fontWeight: '700' }}>Nenhuma aula agendada</div>
                  <div style={{ fontSize: '0.82rem', marginTop: '4px' }}>Reserve uma aula no calendário abaixo!</div>
                </div>
              ) : (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {upcomingLessons.map(l => (
                    <div key={l.id} className="upcoming-item">
                      <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: l.title === 'Speaking Club' ? '#f0fdf4' : l.title === 'Elite Squad' ? '#faf5ff' : '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>
                        {l.title === 'Speaking Club' ? '🗣️' : l.title.includes('Elite') ? '⚡' : l.title === 'Plantão de dúvidas' ? '❓' : '📚'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.88rem' }}>{l.title}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', marginTop: '2px' }}>
                          {new Date(l.class_time).toLocaleString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {l.teacher_name ? ` · ${l.teacher_name}` : ''}
                        </div>
                        {waitlistPositions[l.id] && (
                          <div style={{ fontSize: '0.7rem', color: '#ff6a00', fontWeight: '800', marginTop: '3px' }}>⏳ Lista de espera #{waitlistPositions[l.id]}</div>
                        )}
                      </div>
                      {l.meet_link && (
                        <a href={l.meet_link} target="_blank" rel="noopener noreferrer"
                          style={{ background: '#16a34a', color: 'white', padding: '8px 14px', borderRadius: '10px', fontWeight: '800', fontSize: '0.75rem', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          🎥 Meet
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setUpcomingOpen(false)} style={{ width: '100%', padding: '14px', marginTop: '16px', background: 'white', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>Fechar</button>
            </div>
          </div>
        )}

        {/* Lesson booking modal */}
        {selectedLesson && !cancelModalOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setSelectedLesson(null)}>
            <div className="modal-box">
              <button className="modal-close" onClick={() => setSelectedLesson(null)}>✕</button>
              <div style={{ width: '44px', height: '5px', background: '#e2e8f0', borderRadius: '10px', margin: '0 auto 24px' }} />
              <h2 style={{ fontWeight: '900', fontSize: '1.4rem', color: '#0f172a', margin: '0 0 14px' }}>
                {selectedLesson.extendedProps.isEnrolled ? '📅 Sua Reserva' : '🎟️ Reservar Vaga'}
              </h2>
              <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '14px', marginBottom: '16px', textAlign: 'left', border: '1.5px solid #f1f5f9' }}>
                <p style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: '800', color: '#1a2a6c' }}>{selectedLesson.title.replace('✅ ', '').replace('🚫 ', '')}</p>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.88rem', fontWeight: '600' }}>
                  {new Date(selectedLesson.extendedProps.time).toLocaleString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              {status === '' && (
                <>
                  {selectedLesson.extendedProps.isEnrolled ? (
                    <>
                      {selectedLesson.extendedProps.meet_link && (
                        <a href={selectedLesson.extendedProps.meet_link} target="_blank" rel="noopener noreferrer" className="join-btn-modal">
                          🎥 LINK DA AULA — CLIQUE PARA ENTRAR
                        </a>
                      )}
                      <button onClick={initCancel} className="btn-main btn-cancel-vaga" style={{ marginTop: '10px' }}>CANCELAR MINHA VAGA</button>
                    </>
                  ) : (
                    <>
                      <button onClick={handleAction} className="btn-main" style={{ background: selectedLesson.extendedProps.isFull ? 'linear-gradient(135deg,#ff6a00,#ff8c00)' : 'linear-gradient(135deg,#1a2a6c,#2563eb)' }}>
                        {selectedLesson.extendedProps.isFull ? '📋 ENTRAR NA LISTA DE ESPERA' : '✅ CONFIRMAR AGENDAMENTO'}
                      </button>
                      {waitlistPositions[selectedLesson.id] && (
                        <div style={{ marginTop: '10px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '10px', padding: '10px 14px', fontSize: '0.82rem', fontWeight: '700', color: '#ea580c' }}>
                          ⏳ Você está na posição #{waitlistPositions[selectedLesson.id]} da lista de espera
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {status === 'loading'   && <p style={{ color: '#64748b', fontWeight: '600', marginTop: '16px' }}>Processando...</p>}
              {status === 'success'   && <p style={{ color: '#16a34a', fontWeight: '900', fontSize: '1.1rem', marginTop: '16px' }}>✅ VAGA RESERVADA!</p>}
              {status === 'cancelled' && <p style={{ color: '#ef4444', fontWeight: '900', fontSize: '1.1rem', marginTop: '16px' }}>🗑️ VAGA CANCELADA.</p>}
              {status === 'waitlist'  && <p style={{ color: '#ff6a00', fontWeight: '900', fontSize: '1.1rem', marginTop: '16px' }}>📋 NA LISTA DE ESPERA!</p>}
              {status === 'already'   && <p style={{ color: '#d97706', fontWeight: '700', marginTop: '16px' }}>Você já está inscrito nesta aula.</p>}
            </div>
          </div>
        )}

        {/* Cancellation confirmation modal */}
        {cancelModalOpen && selectedLesson && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setCancelModalOpen(false)}>
            <div className="modal-box" style={{ maxWidth: '400px', textAlign: 'left' }}>
              <button className="modal-close" onClick={() => setCancelModalOpen(false)}>✕</button>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '2.5rem' }}>😕</span>
                <h2 style={{ margin: '10px 0 4px', fontWeight: '900', color: '#0f172a', fontSize: '1.2rem' }}>Cancelar sua vaga?</h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>Sua vaga voltará a ficar disponível para outros alunos.</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '12px 16px', marginBottom: '18px', border: '1.5px solid #f1f5f9' }}>
                <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.88rem' }}>{selectedLesson.title.replace('✅ ', '')}</div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>{new Date(selectedLesson.extendedProps.time).toLocaleString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Motivo (opcional)</div>
                {CANCEL_REASONS.map(r => (
                  <button key={r} onClick={() => setCancelReason(cancelReason === r ? '' : r)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '10px', border: `2px solid ${cancelReason === r ? '#dc2626' : '#f1f5f9'}`, background: cancelReason === r ? '#fee2e2' : '#f8fafc', color: cancelReason === r ? '#dc2626' : '#475569', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', marginBottom: '6px', transition: 'all 0.15s' }}>
                    {r}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setCancelModalOpen(false)} style={{ flex: 1, padding: '13px', background: 'white', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>Manter vaga</button>
                <button onClick={confirmCancel} style={{ flex: 1, padding: '13px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>Sim, cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Speaking Club upsell */}
        {paywallOpen && paywallType === 'sc_upsell' && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setPaywallOpen(false)}>
            <div className="modal-box modal-wide" style={{ padding: '32px 28px' }}>
              <button className="modal-close" onClick={() => setPaywallOpen(false)}>✕</button>
              <div style={{ width: '44px', height: '5px', background: '#e2e8f0', borderRadius: '10px', margin: '0 auto 20px' }} />
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '3rem' }}>🗣️</span>
                <h2 style={{ margin: '10px 0 4px', fontWeight: '900', color: '#0f172a', fontSize: '1.3rem' }}>Speaking Club</h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>Acelere sua fluência com conversação ao vivo</p>
              </div>
              <div style={{ background: '#f0fdf4', borderRadius: '14px', padding: '18px', marginBottom: '16px', border: '1.5px solid #86efac' }}>
                <div style={{ fontWeight: '800', color: '#166634', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>O que você ganha:</div>
                {[['🗣️','100% focado em conversação ao vivo'],['🌍','Temas atuais: business, tech e cultura global'],['🤝','Networking com outros alunos do seu nível'],['👂','Melhora rápida de Listening & Speaking'],['💡','Correções em tempo real pelo professor']].map(([icon,text]) => (
                  <div key={text} className="perk"><span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</span><span style={{ color: '#166534', fontWeight: '600', fontSize: '0.85rem' }}>{text}</span></div>
                ))}
              </div>
              <div style={{ background: 'linear-gradient(135deg,#fff7ed,#fef3c7)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1.5px solid #fbbf24', textAlign: 'center' }}>
                <div style={{ fontWeight: '900', color: '#92400e', fontSize: '1.3rem' }}>+ R$ 20/mês</div>
                <div style={{ color: '#b45309', fontSize: '0.78rem', fontWeight: '600', marginTop: '4px' }}>Mínimo 3 meses de aviso prévio para cancelamento</div>
                <div style={{ color: '#78350f', fontSize: '0.78rem', fontWeight: '700', marginTop: '8px', background: 'white', borderRadius: '8px', padding: '8px 12px' }}>🎁 Peça <strong>1 aula trial gratuita</strong> antes de decidir!</div>
              </div>
              <a href={`${WA}${encodeURIComponent('Oi! Sou aluno Individual VIP e tenho interesse em adicionar o Speaking Club ao meu plano. Podem me dar mais informações?')}`} target="_blank" rel="noopener noreferrer" className="wa-btn">💬 Quero adicionar o Speaking Club</a>
              <a href={`${WA}${encodeURIComponent('Oi! Sou aluno Individual VIP e gostaria de fazer a aula trial gratuita do Speaking Club!')}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderRadius: '14px', fontWeight: '700', fontSize: '0.88rem', textDecoration: 'none', width: '100%', boxSizing: 'border-box', marginTop: '8px', background: '#f0fdf4', color: '#166534', border: '1.5px solid #86efac' }}>🎁 Solicitar aula trial gratuita</a>
              <button className="btn-ghost" onClick={() => setPaywallOpen(false)}>Agora não</button>
            </div>
          </div>
        )}

        {/* Plan upgrade paywall */}
        {paywallOpen && paywallType === 'plan_upgrade' && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setPaywallOpen(false)}>
            <div className="modal-box modal-wide" style={{ padding: '32px 28px' }}>
              <button className="modal-close" onClick={() => setPaywallOpen(false)}>✕</button>
              <div style={{ width: '44px', height: '5px', background: '#e2e8f0', borderRadius: '10px', margin: '0 auto 20px' }} />
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '3rem' }}>⚡</span>
                <h2 style={{ margin: '10px 0 4px', fontWeight: '900', color: '#0f172a', fontSize: '1.3rem' }}>Quer evoluir ainda mais rápido?</h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>Esta aula não está disponível no seu plano atual</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div style={{ background: 'linear-gradient(135deg,#dbeafe,#eff6ff)', border: '2px solid #93c5fd', borderRadius: '14px', padding: '16px' }}>
                  <div style={{ fontWeight: '900', color: '#1d4ed8', marginBottom: '8px', fontSize: '0.9rem' }}>🎯 Individual VIP</div>
                  {['1h/semana com Specialist','Customização total','Suporte WhatsApp Premium','Plantão de dúvidas incluso'].map(p => (
                    <div key={p} style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: '600', marginBottom: '4px', display: 'flex', gap: '6px' }}><span>✓</span><span>{p}</span></div>
                  ))}
                  <div style={{ fontWeight: '900', color: '#1d4ed8', marginTop: '10px', fontSize: '1rem' }}>R$599/mês</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg,#fef3c7,#fffbeb)', border: '2px solid #fbbf24', borderRadius: '14px', padding: '16px', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: '#d97706', color: 'white', padding: '3px 12px', borderRadius: '50px', fontSize: '0.65rem', fontWeight: '800', whiteSpace: 'nowrap' }}>⭐ MAIS COMPLETO</div>
                  <div style={{ fontWeight: '900', color: '#d97706', marginBottom: '8px', fontSize: '0.9rem' }}>🚀 Elite Squad</div>
                  {['12 meses de imersão','Meses 1-2: Individual','Meses 3-12: Squad','Speaking Club incluso','Relatórios mensais'].map(p => (
                    <div key={p} style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: '600', marginBottom: '4px', display: 'flex', gap: '6px' }}><span>✓</span><span>{p}</span></div>
                  ))}
                  <div style={{ fontWeight: '900', color: '#d97706', marginTop: '10px', fontSize: '1rem' }}>R$299/mês</div>
                </div>
              </div>
              <a href={`${WA}${encodeURIComponent('Oi! Sou aluno do Speaking Club e tenho interesse em fazer upgrade do meu plano. Podem me ajudar?')}`} target="_blank" rel="noopener noreferrer" className="wa-btn">💬 Quero fazer upgrade do meu plano</a>
              <button className="btn-ghost" onClick={() => setPaywallOpen(false)}>Agora não</button>
            </div>
          </div>
        )}

        </> /* end portal tab */}

      </div>
    </>
  );
}
